import { useCallback, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest, loginRequest } from './msalConfig';

export interface AuthIdentity {
  email: string;
  name: string;
  /** Rol elegido por el backend según jerarquía (system_admin > ... > student). */
  activeRole:
    | 'student'
    | 'teacher'
    | 'guardian'
    | 'curriculum_admin'
    | 'school_admin'
    | 'system_admin';
  roles: readonly string[];
  // Post-cutover pioneros DB: hex 24 chars (mongo ObjectId de core.site.id).
  currentSiteId: string;
  availableSites: readonly string[];
}

export interface UseAuthResult {
  isAuthenticated: boolean;
  identity: AuthIdentity | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

interface MeResponse {
  user_id: string;
  email: string;
  roles: string[];
  active_role: AuthIdentity['activeRole'];
  available_sites: { id: string }[];
  current_site_id: string;
}

export function useAuth(): UseAuthResult {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? undefined);

  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const r = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // Token expiró o consent revocado; fallback a popup.
        try {
          const r = await instance.acquireTokenPopup({ ...apiTokenRequest, account });
          return r.accessToken;
        } catch {
          return null;
        }
      }
      console.error('[auth] acquireTokenSilent fail:', e);
      return null;
    }
  }, [instance, account]);

  useEffect(() => {
    if (!isAuthenticated || !account) {
      setIdentity(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          if (!cancelled) setError('No se pudo obtener token AD.');
          return;
        }
        const resp = await fetch('/v1/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.status === 403) {
          if (!cancelled) {
            setError(
              'Tu cuenta Pioneros no tiene acceso a esta plataforma. Contacta al equipo.',
            );
            setIdentity(null);
          }
          return;
        }
        if (!resp.ok) {
          throw new Error(`/v1/me HTTP ${resp.status}`);
        }
        const data = (await resp.json()) as MeResponse;
        if (cancelled) return;
        setIdentity({
          email: data.email,
          name: account.name ?? data.email,
          activeRole: data.active_role,
          roles: data.roles,
          currentSiteId: data.current_site_id,
          availableSites: data.available_sites.map((s) => s.id),
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error resolviendo identidad');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, getAccessToken]);

  const login = useCallback(async () => {
    setError(null);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Login falló');
    }
  }, [instance]);

  const logout = useCallback(async () => {
    if (!account) return;
    await instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
  }, [instance, account]);

  return {
    isAuthenticated: isAuthenticated && !!identity,
    identity,
    loading,
    error,
    login,
    logout,
    getAccessToken,
  };
}
